import { useMapStore } from './store';
import { showMapToast } from './mapControls';
import { isLocationWithinServiceArea } from './utils';
import { toast } from 'react-hot-toast';
import { useLockManager, withLock } from './lockManager';

/**
 * Centralized controller for map operations
 * Consolidates common map interaction patterns
 */
export const mapController = {
  // Store references
  _idleListener: null as google.maps.MapsEventListener | null,

  // Add debouncing support
  updatePositionDebounceTimer: null as number | null,
  POSITION_UPDATE_DEBOUNCE: 500, // ms

  // Add type for geocoder
  geocoder: null as google.maps.Geocoder | null,

  /**
   * Update map position with proper handling of events and state
   */
  updateMapPosition(map: google.maps.Map, position: { lat: number; lng: number }, options = { 
    geocode: true, 
    findNearby: true,
    zoom: null as number | null,
    removeListeners: true,
    restoreListeners: true,
    updateMarker: true,
    delay: 1000,
  }) {
    if (!map) {
      console.warn('[mapController] updateMapPosition called without valid map');
      return;
    }

    console.log('[mapController] Updating map position:', position);
    
    // Add debouncing to prevent rapid-fire updates
    if (this.updatePositionDebounceTimer) {
      window.clearTimeout(this.updatePositionDebounceTimer);
      this.updatePositionDebounceTimer = null;
    }
    
    // Get the lock manager from store
    const store = useMapStore.getState();
    const lockManager = useLockManager.getState();
    
    // Lock immediately to prevent other updates during debounce
    if (lockManager) {
      lockManager.lockMultiple(['map', 'position'], 'updateMapPosition');
    }
    
    // For backwards compatibility, still set the old state flag
    useMapStore.setState({ isProgrammaticUpdate: true });
    
    // Remove listeners if requested
    if (options.removeListeners) {
      this.removeMapListeners();
    }
    
    // Update position in store if requested
    if (options.updateMarker) {
      useMapStore.getState().setMarkerPosition(position);
      useMapStore.getState().updatePosition(position);
    }
    
    // Move map to the position
    map.panTo(position);
    map.setCenter(position);
    
    // Set zoom if provided
    if (options.zoom !== null) {
      map.setZoom(options.zoom);
    }
    
    // Force map to redraw
    setTimeout(() => {
      google.maps.event.trigger(map, 'resize');
    }, 100);
    
    // Perform geocoding if requested
    if (options.geocode) {
      this.geocodePosition(position);
    }
    
    // Find nearby places if requested
    if (options.findNearby) {
      this.findNearbyPlaces(map, position);
    }
    
    // Debounce the actual update
    this.updatePositionDebounceTimer = window.setTimeout(() => {
      try {
        console.log('[mapController] Executing debounced position update');
        
        // Get the current state and geocoder
        const store = useMapStore.getState();
        if (!this.geocoder) {
          this.geocoder = new window.google.maps.Geocoder();
        }
        
        // Run geocoder if available
        if (this.geocoder) {
          this.geocoder.geocode({ location: position }, (results, status) => {
            if (status === "OK" && results && results.length > 0) {
              const address = results[0].formatted_address;
              console.log('[mapController] Got address from geocoder:', address);
              
              // Update address in store
              store.setAddress(address);
              
              // Try to extract the location name from the first part of the address
              const locationNameParts = address.split(',');
              if (locationNameParts.length > 0) {
                store.setLocationName(locationNameParts[0].trim());
              }
            } else {
              console.warn('[mapController] Geocoder failed:', status);
            }
            
            // Always reset programmatic update flag
            useMapStore.setState({ isProgrammaticUpdate: false });
            
            // Unlock map if lockManager is available
            const lockManager = useLockManager.getState();
            lockManager.unlockMultiple(['map', 'position'], 'updateMapPosition complete');
          });
        } else {
          // No geocoder available, just reset flags
          useMapStore.setState({ isProgrammaticUpdate: false });
          
          // Unlock map if lockManager is available
          const lockManager = useLockManager.getState();
          lockManager.unlockMultiple(['map', 'position'], 'updateMapPosition complete (no geocoder)');
        }
      } catch (error) {
        console.error('[mapController] Error in debounced update:', error);
        
        // Reset flags in case of error
        useMapStore.setState({ isProgrammaticUpdate: false });
        
        // Unlock map if lockManager is available
        const lockManager = useLockManager.getState();
        lockManager.unlockMultiple(['map', 'position'], 'updateMapPosition error');
      }
    }, this.POSITION_UPDATE_DEBOUNCE);
  },

  /**
   * Geocode a position and update store with result
   */
  geocodePosition(position: { lat: number; lng: number }) {
    const store = useMapStore.getState();
    
    // Set loading state - try to get translated message from component if available
    const loadingMessage = 'Loading address...';
    store.setAddress(loadingMessage);
    
    // Perform geocoding
    store.geocodePosition(position)
      .then(result => {
        store.setAddress(result.address);
        store.setLocationName(result.locationName);
        
        // Check if within service area
        const isWithin = this.checkIsWithinServiceArea(position);
        store.setIsWithinServiceArea(isWithin);
        
        return { result, isWithin };
      })
      .catch(error => {
        console.error('[mapController] Geocoding error:', error);
        const errorMessage = 'Address not found';
        const unknownLocationMessage = 'Unknown location';
        
        store.setAddress(errorMessage);
        store.setLocationName(unknownLocationMessage);
      });
  },

  /**
   * Find nearby places and update store
   */
  findNearbyPlaces(map: google.maps.Map, position: { lat: number; lng: number }) {
    const store = useMapStore.getState();
    const currentAddress = store.address;
    
    // Add a static property to track last search position
    const lastSearchPosition = (this as any)._lastNearbySearchPosition;
    
    // Add position comparison to prevent redundant searches (increased threshold to reduce API calls)
    if (lastSearchPosition) {
      const isNearbyIdentical = 
        Math.abs(position.lat - lastSearchPosition.lat) < 0.0005 && 
        Math.abs(position.lng - lastSearchPosition.lng) < 0.0005;
      
      if (isNearbyIdentical) {
        console.log('[mapController] Skipping nearby search - position too similar to previous search');
        return;
      }
    }
    
    // Store the current position for future comparison
    (this as any)._lastNearbySearchPosition = { ...position };
    
    // Use the store's findNearbyPlacesModern function
    store.findNearbyPlacesModern(map, position, currentAddress)
      .then(places => {
        if (places && places.length > 0) {
          store.setAlternateAddresses(places);
          console.log('[mapController] Updated nearby places:', places.length);
        }
      })
      .catch(error => {
        console.error('[mapController] Error finding nearby places:', error);
      });
  },

  /**
   * Check if a location is within the service area
   * Uses the standardized utility function to ensure consistency across the application
   */
  checkIsWithinServiceArea(position: { lat: number; lng: number }): boolean {
    return isLocationWithinServiceArea(position);
  },

  /**
   * Add map idle listener
   */
  addMapListeners(map: google.maps.Map) {
    if (!map) return;
    
    // Check lock states using the lock manager
    const lockManager = useLockManager.getState();
    
    if (lockManager.isLocked('address')) {
      console.log('[mapController] Not adding idle listener - address is locked');
      return;
    }
    
    if (lockManager.isLocked('map') || lockManager.isLocked('position')) {
      console.log('[mapController] Not adding idle listener - map or position is locked');
      return;
    }
    
    // For backwards compatibility, also check the old state flags
    const mapStore = useMapStore.getState();
    if (mapStore.isAddressLocked) {
      console.log('[mapController] Not adding idle listener - address is locked (legacy flag)');
      return;
    }
    
    if (mapStore.isProgrammaticUpdate) {
      console.log('[mapController] Not adding idle listener - programmatic update in progress (legacy flag)');
      return;
    }
    
    // Remove existing listener if any
    this.removeMapListeners();
    
    // Add fresh listener
    const handleMapMoveEnd = () => {
      if (!map) return;
      
      const center = map.getCenter();
      if (!center) return;
      
      // Check if any locks are already active - if so, skip update
      const lockManager = useLockManager.getState();
      if (lockManager.isLocked('map') || lockManager.isLocked('position')) {
        console.log('[mapController] Skipping map move update - map or position already locked');
        return;
      }
      
      const newPosition = {
        lat: center.lat(),
        lng: center.lng()
      };
      
      // Update position without removing listeners (would cause a loop)
      // Use a special version for idle events to avoid lock issues
      this.updateMapPositionLite(map, newPosition);
    };
    
    this._idleListener = map.addListener('idle', handleMapMoveEnd);
    console.log('[mapController] Added idle listener for map movements');
  },

  /**
   * Remove map idle listener
   */
  removeMapListeners() {
    if (this._idleListener) {
      google.maps.event.removeListener(this._idleListener);
      this._idleListener = null;
      console.log('[mapController] Removed idle listener');
    }
  },

  /**
   * Handle user using current location
   */
  useCurrentLocation(map: google.maps.Map) {
    const store = useMapStore.getState();
    
    store.getUserLocation()
      .then(position => {
        console.log('[mapController] Got current location:', position);
        
        // Update map with current location
        this.updateMapPosition(map, position, {
          geocode: true,
          findNearby: true,
          zoom: 18,
          removeListeners: true,
          restoreListeners: true,
          updateMarker: true,
          delay: 1000
        });
        
        // Check if within service area
        const isWithin = this.checkIsWithinServiceArea(position);
        store.setIsWithinServiceArea(isWithin);
        
        // Show warning if outside service area
        if (!isWithin && map) {
          const warningMessage = 'This location is outside our delivery area';
          showMapToast({ current: map }, warningMessage, 5000);
        }
      })
      .catch(error => {
        console.error('[mapController] Error getting current location:', error);
        const errorCode = error.code ? `(${error.code}) ` : '';
        const errorDetails = error.message || 'Unknown error';
        
        // Show error toast
        toast.error(`Location error: ${errorCode}${errorDetails}`);
      });
  },

  /**
   * Lighter version of updateMapPosition specifically for idle events
   * Avoids the locking issues that can cause infinite loading
   */
  updateMapPositionLite(map: google.maps.Map, position: { lat: number; lng: number }) {
    if (!map) return;
    
    console.log('[mapController] Lite update map position:', position);
    
    // Don't lock the whole map - that can cause problems with drag events
    // Only do minimal updates to avoid race conditions
    
    // Update position in store
    useMapStore.getState().setMarkerPosition(position);
    useMapStore.getState().updatePosition(position);
    
    // Perform geocoding
    this.geocodePositionLite(position);
    
    // Find nearby places without locking
    this.findNearbyPlacesLite(map, position);
  },
  
  /**
   * Lighter version of geocodePosition that doesn't use locks
   */
  geocodePositionLite(position: { lat: number; lng: number }) {
    const store = useMapStore.getState();
    
    // Set loading state
    const loadingMessage = 'Loading address...';
    store.setAddress(loadingMessage);
    
    // Perform geocoding without setting locks
    store.geocodePosition(position)
      .then(result => {
        store.setAddress(result.address);
        store.setLocationName(result.locationName);
        
        // Check if within service area
        const isWithin = this.checkIsWithinServiceArea(position);
        store.setIsWithinServiceArea(isWithin);
      })
      .catch(error => {
        console.error('[mapController] Geocoding error:', error);
        const errorMessage = 'Address not found';
        const unknownLocationMessage = 'Unknown location';
        
        store.setAddress(errorMessage);
        store.setLocationName(unknownLocationMessage);
      });
  },
  
  /**
   * Lighter version of findNearbyPlaces that doesn't use locks
   */
  findNearbyPlacesLite(map: google.maps.Map, position: { lat: number; lng: number }) {
    const store = useMapStore.getState();
    const currentAddress = store.address;
    
    // Add a static property to track last search position
    const lastSearchPosition = (this as any)._lastLiteSearchPosition;
    
    // Add position comparison to prevent redundant searches (increased threshold to reduce API calls)
    if (lastSearchPosition) {
      const isNearbyIdentical = 
        Math.abs(position.lat - lastSearchPosition.lat) < 0.0005 && 
        Math.abs(position.lng - lastSearchPosition.lng) < 0.0005;
      
      if (isNearbyIdentical) {
        console.log('[mapController] Lite - Skipping nearby search - position too similar to previous search');
        return;
      }
    }
    
    // Store the current position for future comparison
    (this as any)._lastLiteSearchPosition = { ...position };
    
    // Use the store's findNearbyPlacesModern function without setting locks
    store.findNearbyPlacesModern(map, position, currentAddress)
      .then(places => {
        if (places && places.length > 0) {
          store.setAlternateAddresses(places);
          console.log('[mapController] Lite update - nearby places updated:', places.length);
        }
      })
      .catch(error => {
        console.error('[mapController] Lite update - Error finding nearby places:', error);
      });
  },
};

// Make toast available globally for the error handler
declare global {
  interface Window {
    toast?: any;
  }
} 